import { useQuery } from "@tanstack/react-query";
import AwsApiClient from "../clients/awsApiClient.ts";
import type { ApiRequestConfig, ApiError } from "../clients/awsApiClient.ts";

export type StripePrice = {
    id: string;
    unit_amount: number;
    currency: string;
    recurring?: {
        interval: string;
        interval_count: number;
    } | null;
};

export type Subscription = {
    id: string;
    name?: string;
    productName: string;
    description?: string;
    /** All active prices for the product (one per currency / billing interval). */
    prices: StripePrice[];
    /** Stripe's configured default price id, if any. */
    default_price?: string;
    metadata?: {
        translation_key?: string;
        base_permission?: string;
        index?: string;
        [key: string]: string | undefined;
    };
};

// Tolerate a backend that still returns a single `prices` object (pre-array
// deploy) so the frontend and Lambda don't have to ship in lockstep.
type RawSubscription = Omit<Subscription, "prices"> & {
    prices: StripePrice | StripePrice[] | null | undefined;
};

const normalizePrices = (raw: RawSubscription): Subscription => ({
    ...raw,
    prices: Array.isArray(raw.prices)
        ? raw.prices
        : raw.prices
          ? [raw.prices]
          : [],
});

export function useSubscriptions(config?: ApiRequestConfig) {
    return useQuery<RawSubscription[], ApiError, Subscription[]>({
        queryKey: ["subscriptions"],
        queryFn: () =>
            AwsApiClient.apiGet<RawSubscription[]>("/stripe/subscriptions", config),
        select: (data) => data.map(normalizePrices),
    });
}

export interface CheckoutItem {
    priceId: string;
    quantity: number;
}
